import { createFileRoute, notFound } from "@tanstack/react-router";
import StageScene from "@/features/viewer/components/StageScene";
import type { StageEffectFlags } from "@/features/viewer/degradation";
import {
  DEFAULT_STAGE_CONFIG,
  type StageConfig,
  TONE_MAPPING_MODES,
  type ToneMappingKey,
} from "@/features/viewer/stage-config";
import { decodeCfg, deepMerge } from "@/features/viewer/stageConfigMerge";

// DEV-ONLY deterministic screenshot route (issue #92): a stripped surface that
// renders the PRODUCTION Stage (the same <StageScene> + DEFAULT_STAGE_CONFIG the
// <Viewer> ships) with no chrome, no job-stream, no DOM controls — just the
// canvas at a query-param-controlled, physics-free state. The `pnpm shot`
// harness (scripts/shot.mjs) drives headless Chromium against this route and
// reads back the PNGs, so agents can SEE a viewer change instead of editing it
// blind. Guarded on import.meta.env.DEV (like /stage-lab) so it never ships.
//
// Determinism (ADR-0041 §4): there is no physics anywhere — the cushion holds the
// deterministic rest pose, so by default reducedMotion is ON and the reveal snaps
// to its end state, a transform identical run-to-run. (For reveal-beat frames a
// `reveal` t turns motion on to honour that locked progress; the pose is still
// static, so still no timer-dependent frame.) Combined with seeded SSAO, shots
// are byte-identical run-to-run. (#162: the film grain that USED to break this —
// a per-frame random pass the shot had to disable — is gone; the anti-banding
// dither that replaced it is a static per-pixel function, so it's deterministic
// and the shot now renders full production parity.)

// The canned mock fixture: the committed real-looking sample pillow in public/.
// Always available, deterministic, no pipeline run needed — the default. The
// `local` fixture (a real local-pipeline artifact set from LocalStack) is
// resolved by the harness, which discovers the URLs and passes them explicitly.
const MOCK_MESH_URL = "/pillow-sample.glb";
// The pillow TEXTURE is the worker's real `texture_front` — the bbox-tight pet
// CUTOUT (WebP, alpha), matched to the mesh's crop-remapped UVs (ADR-0033 §3). NOT
// the full input photo `pillow-sample-pet.jpg` (that's the landing's "before" image,
// content.ts) — using the full photo here mapped the photo's background onto the
// silhouette (the white-step bleed) AND the old GLB was a stale pre-#156 bake whose
// form rendered upside down. Refreshed from the current worker (#188).
const MOCK_TEXTURE_FRONT_URL = "/pillow-sample-pet.webp";

// The harness driver polls this flag and shoots once it's true (see scripts/shot.mjs).
declare global {
  interface Window {
    __shotReady?: boolean;
  }
}

// Full production parity (#162): with the per-frame film grain retired and its
// anti-banding role taken over by a static, deterministic output dither, there is
// nothing time-varying left to disable — the shot renders exactly what ships.
const SHOT_CONFIG: StageConfig = DEFAULT_STAGE_CONFIG;

// #188 look sign-off: a generic DEV-only look override. The real-GPU CDP harness
// (.scratch/188) base64-encodes a PARTIAL StageConfig and passes it as `cfg`; we
// deep-merge it over DEFAULT_STAGE_CONFIG so ANY look axis (backdrop hues, accent
// colour/strength, vignette, fog gradient, contact-shadow softness, lean, framing)
// can be A/B'd deterministically on the real pet — instead of bolting on a one-off
// param per knob. The existing scalar overrides (keyI, petExp, …) still layer ON TOP
// of the merged base, so older harness scripts keep working. DEV-gated with the
// route — never ships. The merge itself (deepMerge/decodeCfg) lives in the unit-tested
// stageConfigMerge module (#196), since the realism render rig's deterministic A/B
// hinges on that contract.

export type ShotFixture = "mock" | "local" | "empty";
export type ShotAngle = "front" | "three-quarter" | "flipped-back";

interface ShotSearch {
  fixture: ShotFixture;
  angle: ShotAngle;
  // Lock the reveal pose to a fixed progress (0..1) for reveal-beat frames;
  // omitted = 1 (the settled rest pose).
  reveal?: number;
  // Explicit artifact URLs (the harness passes these for the `local` fixture
  // after discovering the newest LocalStack project). Override the fixture.
  mesh?: string;
  texFront?: string;
  texBack?: string;
  petBg?: string;
  // #122 toggle matrix — one-variable-at-a-time overrides over SHOT_CONFIG.
  // fabric/ssao/bloom force one laddered effect; tone swaps the curve; post=0
  // unmounts the whole EffectComposer (renderer-default ACES then applies). All
  // omitted = the (now production-parity) default shot.
  fabric?: boolean;
  ssao?: boolean;
  bloom?: boolean;
  post?: boolean;
  tone?: ToneMappingKey;
  // Key-spot intensity override (the #122 exposure sweep).
  keyI?: number;
  // Kicker (grazing rake) intensity override (#165 light-first A/B): 0 turns the
  // kicker OFF to show the Gathers going flat without it, vs the default that rakes
  // them — the before/after the SSAO-off mid-range path hinges on.
  kickI?: number;
  // Pet exposure compensation override (the #122 contact sheet).
  petExp?: number;
  // Procedural weave density override (cycles per UV unit) — the #124 thread-scale
  // before/after. Omitted = the calibrated production density.
  weaveD?: number;
  // #168 weave-reprocess live sweep: procedural weave normal amplitude, and the
  // fwidth band-limit persistence (1.0 = original Nyquist roll-off; higher = the
  // weave survives to mid distance). Both omitted = the production fabric values.
  weaveDepth?: number;
  weaveFade?: number;
  // #168: render-time normal-map strength multiplier (the baked-relief boost).
  nrm?: number;
  // ADR-0045 object-turntable: rotate the CUSHION (not the camera — the camera is
  // fixed) to this absolute YAW (degrees, 0 = the home front face). Generalises
  // `angle` so the harness can shoot the back (az=180), a side (az=90), etc.
  az?: number;
  // ADR-0045: tilt the cushion to this absolute PITCH (degrees; +tips the top toward
  // the camera, − reveals the underside). Omitted = 0 (the home lean). Replaces the
  // old camera-polar param — with a fixed camera, "see the top/underside" is a
  // cushion pitch, not a camera elevation.
  polar?: number;
  // #188 look sign-off: base64-encoded partial StageConfig, deep-merged over the
  // defaults (decodeCfg/deepMerge). The generic A/B knob for the whole warm-sweep look.
  cfg?: string;
  // #216 DEBUG: enable live interaction (drag/zoom) on the production Stage with the real
  // pillow — disables the fixed shot orientation and turns motion on. For reproducing
  // interaction bugs on the real wiring (the CDP repro harness), never a rendered shot.
  live?: boolean;
}

// A 0/1 (or boolean — the router JSON-parses search values) diagnostic flag;
// anything else = no override.
function asFlag(value: unknown): boolean | undefined {
  if (value === 1 || value === "1" || value === true) return true;
  if (value === 0 || value === "0" || value === false) return false;
  return undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export const Route = createFileRoute("/shot")({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>): ShotSearch => {
    const fixture: ShotFixture =
      search.fixture === "local" ? "local" : search.fixture === "empty" ? "empty" : "mock";
    const angle: ShotAngle =
      search.angle === "front" || search.angle === "flipped-back" ? search.angle : "three-quarter";
    const revealRaw = Number(search.reveal);
    const reveal = Number.isFinite(revealRaw) ? Math.min(1, Math.max(0, revealRaw)) : undefined;
    const toneRaw = asString(search.tone);
    return {
      fixture,
      angle,
      reveal,
      mesh: asString(search.mesh),
      texFront: asString(search.texFront),
      texBack: asString(search.texBack),
      petBg: asString(search.petBg),
      fabric: asFlag(search.fabric),
      ssao: asFlag(search.ssao),
      bloom: asFlag(search.bloom),
      post: asFlag(search.post),
      tone: toneRaw && toneRaw in TONE_MAPPING_MODES ? (toneRaw as ToneMappingKey) : undefined,
      keyI: Number.isFinite(Number(search.keyI)) ? Number(search.keyI) : undefined,
      kickI:
        search.kickI != null && Number.isFinite(Number(search.kickI))
          ? Number(search.kickI)
          : undefined,
      petExp: Number.isFinite(Number(search.petExp)) ? Number(search.petExp) : undefined,
      weaveD:
        search.weaveD != null && Number.isFinite(Number(search.weaveD))
          ? Number(search.weaveD)
          : undefined,
      weaveDepth:
        search.weaveDepth != null && Number.isFinite(Number(search.weaveDepth))
          ? Number(search.weaveDepth)
          : undefined,
      weaveFade:
        search.weaveFade != null && Number.isFinite(Number(search.weaveFade))
          ? Number(search.weaveFade)
          : undefined,
      nrm:
        search.nrm != null && Number.isFinite(Number(search.nrm)) ? Number(search.nrm) : undefined,
      az: search.az != null && Number.isFinite(Number(search.az)) ? Number(search.az) : undefined,
      polar:
        search.polar != null && Number.isFinite(Number(search.polar))
          ? Number(search.polar)
          : undefined,
      cfg: asString(search.cfg),
      live: asFlag(search.live),
    };
  },
  component: ShotStage,
});

function ShotStage() {
  const { fixture, angle, reveal, mesh, texFront, texBack, petBg, ...matrix } = Route.useSearch();

  // #188: the generic look override (base64 partial config) is the MERGE BASE; the
  // #122/#165/#187 scalar overrides then layer on top of it (so a script can set the
  // hues via cfg AND sweep keyI/petExp on the same shot). No memo — the route remounts
  // per navigation in the harness, and an object identity change only re-renders the
  // scene with the same values.
  const base = deepMerge(SHOT_CONFIG, decodeCfg(matrix.cfg));
  const config: StageConfig = {
    ...base,
    lighting: {
      ...base.lighting,
      key: {
        ...base.lighting.key,
        intensity: matrix.keyI ?? base.lighting.key.intensity,
      },
      kicker: {
        ...base.lighting.kicker,
        intensity: matrix.kickI ?? base.lighting.kicker.intensity,
      },
    },
    pillow: {
      ...base.pillow,
      exposure: matrix.petExp ?? base.pillow.exposure,
      normalScale: matrix.nrm ?? base.pillow.normalScale,
      fabric: {
        ...base.pillow.fabric,
        weaveDensity: matrix.weaveD ?? base.pillow.fabric.weaveDensity,
        weaveDepth: matrix.weaveDepth ?? base.pillow.fabric.weaveDepth,
        weaveFade: matrix.weaveFade ?? base.pillow.fabric.weaveFade,
      },
    },
    post: {
      ...base.post,
      toneMapping: matrix.tone ?? base.post.toneMapping,
    },
  };
  const effectsOverride: Partial<StageEffectFlags> = {};
  if (matrix.fabric !== undefined) effectsOverride.pillowFabric = matrix.fabric;
  if (matrix.ssao !== undefined) effectsOverride.ssao = matrix.ssao;
  if (matrix.bloom !== undefined) effectsOverride.bloom = matrix.bloom;

  // The empty-Stage fixture (?fixture=empty): no pillow asset at all — the lit warm
  // sweep holds the frame on its own, no cushion.
  const empty = fixture === "empty";

  // Explicit URLs (the `local` fixture) win; otherwise fall back to the canned
  // mock sample. The mesh is the one hard requirement to render the real pillow.
  const meshUrl = empty ? undefined : (mesh ?? MOCK_MESH_URL);
  const textureFrontUrl = empty
    ? undefined
    : (texFront ?? (mesh ? undefined : MOCK_TEXTURE_FRONT_URL));

  // ADR-0045 object-turntable: the camera is FIXED — a canonical "angle" is the
  // CUSHION's orientation, not a camera position. "front" = the home face (yaw 0);
  // "three-quarter" = a pleasing yaw off-front; "flipped-back" = a half-turn to the
  // back. An explicit `az`/`polar` overrides the yaw/pitch with any absolute angle
  // (e.g. az=180 for the back, polar=-90 for the underside).
  const angleYawDeg = angle === "flipped-back" ? 180 : angle === "three-quarter" ? 35 : 0;
  // #216 DEBUG: with ?live=1 the fixed orientation is dropped so the object-turntable
  // input layer is live (drag/zoom), for reproducing interaction bugs on the real wiring.
  const live = matrix.live === true;
  const shotOrientationDeg = live
    ? undefined
    : {
        yaw: matrix.az ?? angleYawDeg,
        pitch: matrix.polar ?? 0,
        roll: 0,
      };

  return (
    <div className="h-screen w-screen" data-testid="shot-stage">
      <StageScene
        config={config}
        effectsOverride={effectsOverride}
        postEnabled={matrix.post ?? true}
        meshUrl={meshUrl}
        ghost={empty}
        textureFrontUrl={textureFrontUrl}
        textureBackUrl={texBack}
        petBackgroundColor={petBg}
        // Default (no `reveal` param) snaps to the rest pose (ADR-0041 §4) — the
        // deterministic default. With a locked `reveal` t, motion must be ON so
        // the Stage honours that progress instead of forcing p=1; the pose is
        // still static (no auto-play), so the frame stays deterministic.
        reducedMotion={reveal === undefined && !live}
        revealProgress={reveal ?? 1}
        revealPlayToken={0}
        onSettled={() => {
          if (typeof window !== "undefined") window.__shotReady = true;
        }}
        // ADR-0045: hold the CUSHION at this fixed orientation for a deterministic
        // shot (the camera is fixed; the object turns).
        shotOrientationDeg={shotOrientationDeg}
      />
    </div>
  );
}
